import { useEffect, useState } from "react";
import { CreditCard, MessageCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_LABEL = {
  sinal_projeto: "Sinal do Projeto",
  site_empresarial: "Site Empresarial",
  site_games: "Site de Jogo / eSports",
  manutencao_mensal: "Manutenção Mensal",
};

const STATUS_STYLE = {
  paid: "border-emerald-500/40 bg-emerald-500/10 text-emerald-400",
  pending: "border-amber-500/40 bg-amber-500/10 text-amber-400",
  failed: "border-[#E50914]/40 bg-[#E50914]/10 text-[#FF2A3B]",
  expired: "border-zinc-600 bg-zinc-800 text-zinc-400",
  refunded: "border-zinc-600 bg-zinc-800 text-zinc-400",
};

const formatBRL = (cents) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format((cents || 0) / 100);

const formatDate = (iso) => {
  try {
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return "";
  }
};

export const AdminPayments = () => {
  const [payments, setPayments] = useState(null);

  useEffect(() => {
    fetch(`${API}/admin/payments`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setPayments(d.payments || []))
      .catch(() => setPayments([]));
  }, []);

  const totalPaid = (payments || []).filter((p) => p.payment_status === "paid").reduce((s, p) => s + (p.amount || 0), 0);

  return (
    <div className="mt-10 rounded-2xl border border-[#22222A] bg-[#101014]">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#1A1A20] px-6 py-4">
        <div className="flex items-center gap-3">
          <CreditCard className="h-5 w-5 text-[#E50914]" />
          <div>
            <h2 className="font-display text-lg font-bold text-zinc-100">Pagamentos</h2>
            <p className="text-xs text-zinc-500">Transações do checkout Stripe</p>
          </div>
        </div>
        <span className="font-mono-code text-sm text-emerald-400" data-testid="admin-payments-total">
          {formatBRL(totalPaid)} recebidos
        </span>
      </div>
      <div className="space-y-3 p-4 sm:p-6" data-testid="admin-payments-list">
        {payments === null ? (
          <p className="py-8 text-center text-sm text-zinc-600">Carregando...</p>
        ) : payments.length === 0 ? (
          <p className="py-8 text-center text-sm text-zinc-600">Nenhum pagamento ainda.</p>
        ) : (
          payments.map((p) => (
            <div
              key={p.session_id}
              data-testid={`admin-payment-${p.session_id.slice(-6)}`}
              className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#1A1A20] bg-[#0A0A0C] p-4"
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-zinc-100">
                  {SERVICE_LABEL[p.lookup_key] || p.lookup_key}
                  {p.source === "planos" && (
                    <span className="rounded-full border border-[#2A2A33] px-2 py-0.5 font-mono-code text-[9px] uppercase tracking-widest text-zinc-500">
                      via Planos
                    </span>
                  )}
                </div>
                <div className="mt-0.5 font-mono-code text-[11px] text-zinc-500">
                  {p.customer_name ? `${p.customer_name} · ` : ""}
                  {p.email || "visitante"} · {formatDate(p.created_at)}
                </div>
                {p.customer_phone && (
                  <a
                    data-testid={`admin-payment-whatsapp-${p.session_id.slice(-6)}`}
                    href={`https://wa.me/${p.customer_phone.replace(/\D/g, "").replace(/^(?!55)/, "55")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1.5 inline-flex items-center gap-1.5 font-mono-code text-[11px] text-emerald-400 transition-colors hover:text-emerald-300"
                  >
                    <MessageCircle className="h-3 w-3" />
                    {p.customer_phone}
                  </a>
                )}
              </div>
              <div className="flex items-center gap-3">
                <span className="font-display text-base font-extrabold text-zinc-100">{formatBRL(p.amount)}</span>
                <span className={`rounded-full border px-3 py-1 font-mono-code text-[10px] uppercase tracking-widest ${STATUS_STYLE[p.payment_status] || STATUS_STYLE.pending}`}>
                  {p.payment_status}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
